import { useState, useEffect } from 'react';
import { useAction, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { EmailPreviewData, RecipientType } from '../types';
import ConfirmModal from './ConfirmModal';
import './DMCAEmailModal.css';

interface DMCAEmailModalProps {
  domain: string;
  onClose: () => void;
}

export default function DMCAEmailModal({ domain, onClose }: DMCAEmailModalProps) {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientType, setRecipientType] = useState<RecipientType>('hosting');
  const [fromEmail, setFromEmail] = useState('legal@osn.com');
  const [fromName, setFromName] = useState('OSN Legal Team');
  const [showPreview, setShowPreview] = useState(false);
  const [emailPreview, setEmailPreview] = useState<EmailPreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const generateEmail = useAction(api.dmca.generateDMCAEmail);
  const sendEmail = useAction(api.dmca.sendDMCAEmail);
  const sentEmails = useQuery(api.dmca.getEmailsForDomain, { domain });

  // Lock body scroll when modal opens (but DON'T scroll the page)
  useEffect(() => {
    // Lock body scroll to prevent scrolling page behind modal
    document.body.style.overflow = 'hidden';

    // Restore body scroll on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handlePreview = async () => {
    if (!recipientEmail.trim()) {
      setError('Please enter a recipient email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const preview = await generateEmail({
        domain,
        recipientEmail: recipientEmail.trim(),
        recipientType,
      });
      setEmailPreview(preview);
      setShowPreview(true);
    } catch (err: any) {
      setError(err.message || 'Failed to generate email preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSendClick = () => {
    if (!recipientEmail.trim()) {
      setError('Please enter a recipient email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    // Show confirmation modal
    setShowConfirm(true);
  };

  const handleConfirmSend = async () => {
    setShowConfirm(false);
    setLoading(true);
    setError(null);

    try {
      await sendEmail({
        domain,
        recipientEmail: recipientEmail.trim(),
        recipientType,
        fromEmail: fromEmail.trim() || undefined,
        fromName: fromName.trim() || undefined,
      });
      setSuccess(true);
    } catch (err: any) {
      if (err.message.includes('RESEND_API_KEY')) {
        setError('Resend API key not configured. Please add RESEND_API_KEY to your environment variables.');
      } else {
        setError(err.message || 'Failed to send email');
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-close modal after success with cleanup
  useEffect(() => {
    if (success) {
      const timeoutId = setTimeout(() => {
        onClose();
      }, 2000);

      // Cleanup timeout if component unmounts
      return () => clearTimeout(timeoutId);
    }
  }, [success, onClose]);

  const getRecipientTypeName = (type: string) => {
    switch (type) {
      case 'hosting': return 'Hosting Provider';
      case 'registrar': return 'Domain Registrar';
      case 'domain': return 'Domain Owner';
      default: return type;
    }
  };

  if (success) {
    return (
      <div className="dmca-modal-overlay" onClick={onClose}>
        <div className="dmca-modal" onClick={(e) => e.stopPropagation()}>
          <div className="success-message">
            <div className="success-icon">✅</div>
            <h2>DMCA Notice Sent Successfully</h2>
            <p>The email has been sent to {recipientEmail}</p>
            <button className="btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (showPreview && emailPreview) {
    return (
      <div className="dmca-modal-overlay" onClick={onClose}>
        <div className="dmca-modal dmca-modal-large" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>📧 Email Preview</h2>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>

          <div className="preview-info">
            <div className="info-row">
              <strong>From:</strong> {fromName} &lt;{fromEmail}&gt;
            </div>
            <div className="info-row">
              <strong>To:</strong> {recipientEmail}
            </div>
            <div className="info-row">
              <strong>Subject:</strong> {emailPreview.subject}
            </div>
          </div>

          <div className="preview-tabs">
            <button className="tab-btn active">HTML Preview</button>
          </div>

          <div className="preview-content">
            <iframe
              srcDoc={emailPreview.html}
              title="Email Preview"
              className="email-preview-iframe"
            />
          </div>

          <div className="modal-actions">
            <button
              className="btn-secondary"
              onClick={() => setShowPreview(false)}
              disabled={loading}
            >
              ← Back to Edit
            </button>
            <button
              className="btn-primary"
              onClick={handleSendClick}
              disabled={loading}
            >
              {loading ? 'Sending...' : '📤 Send Email'}
            </button>
          </div>

          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dmca-modal-overlay" onClick={onClose}>
      <div className="dmca-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚖️ Send DMCA Takedown Notice</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="info-banner">
            <strong>Domain:</strong> {domain}
          </div>

          <div className="form-section">
            <h3>Recipient Information</h3>

            <div className="form-group">
              <label htmlFor="recipient-type">Recipient Type</label>
              <select
                id="recipient-type"
                value={recipientType}
                onChange={(e) => setRecipientType(e.target.value as RecipientType)}
                className="form-control"
              >
                <option value="hosting">Hosting Provider</option>
                <option value="registrar">Domain Registrar</option>
                <option value="domain">Domain Owner</option>
              </select>
              <small className="form-hint">
                {recipientType === 'hosting' && 'Send to the hosting provider\'s abuse department'}
                {recipientType === 'registrar' && 'Send to the domain registrar\'s legal team'}
                {recipientType === 'domain' && 'Send directly to the domain owner'}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="recipient-email">Recipient Email *</label>
              <input
                id="recipient-email"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="abuse@example.com"
                className="form-control"
                required
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Sender Information</h3>

            <div className="form-group">
              <label htmlFor="from-name">From Name</label>
              <input
                id="from-name"
                type="text"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="OSN Legal Team"
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label htmlFor="from-email">From Email</label>
              <input
                id="from-email"
                type="email"
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
                placeholder="legal@osn.com"
                className="form-control"
              />
            </div>
          </div>

          {sentEmails && sentEmails.length > 0 && (
            <div className="previous-emails-section">
              <h3>📜 Previous Emails Sent ({sentEmails.length})</h3>
              <div className="previous-emails-list">
                {sentEmails.slice(0, 3).map((email) => (
                  <div key={email._id} className={`email-item email-${email.status}`}>
                    <div className="email-header">
                      <span className="email-status-badge">{email.status}</span>
                      <span className="email-date">
                        {new Date(email.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="email-details">
                      <strong>To:</strong> {email.recipientEmail} ({getRecipientTypeName(email.recipientType)})
                    </div>
                    {email.failureReason && (
                      <div className="email-error">
                        <small>{email.failureReason}</small>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn-preview"
            onClick={handlePreview}
            disabled={loading || !recipientEmail.trim()}
          >
            {loading ? 'Generating...' : '👁️ Preview Email'}
          </button>
          <button
            className="btn-primary"
            onClick={handleSendClick}
            disabled={loading || !recipientEmail.trim()}
          >
            {loading ? 'Sending...' : '📤 Send Directly'}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirm}
        title="Confirm DMCA Notice"
        message={`Are you sure you want to send this DMCA takedown notice to ${recipientEmail}? This action will send a legal notice.`}
        confirmText="Send Notice"
        cancelText="Cancel"
        confirmStyle="danger"
        onConfirm={handleConfirmSend}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
}
